import { useCallback, useEffect, useRef, useState } from "react";

/** Browser speech-to-text (Web Speech API). Norwegian bokmål model is the closest available for nynorsk speech. */
export function useSpeech({ onFinal, lang = "nb-NO" } = {}) {
  const Rec = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
  const supported = !!Rec;
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback(() => {
    if (!Rec) return;
    setError(null);
    const rec = new Rec();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let finalText = "";
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t + " ";
        else interimText += t;
      }
      if (finalText && onFinalRef.current) onFinalRef.current(finalText.trim());
      setInterim(interimText);
    };
    rec.onerror = (e) => { setError(e.error === "not-allowed" ? "Mikrofon ikkje tillaten. Gje nettlesaren tilgang." : e.error); setListening(false); };
    rec.onend = () => { setListening(false); setInterim(""); };
    recRef.current = rec;
    rec.start();
    setListening(true);
  }, [Rec, lang]);

  useEffect(() => () => { try { recRef.current?.abort(); } catch (_) { /* noop */ } }, []);

  return { supported, listening, interim, error, start, stop, toggle: () => (listening ? stop() : start()) };
}
